import { useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  BarChart,
  BarChartProps,
  ComparisonLegend,
  Spinner,
  StackedGroupBarChart,
  StackedGroupBarChartProps,
  TIMP_CHART_COLORS,
  useBarChartPngExport,
  useBarLegendState,
  usePngExport,
  useStackedGroupBarChartPngExport
} from '@jana-technology/platform-ui'

import { useAppSelector } from 'app/hooks'
import { useBaselineInfoQuery } from 'app/services/api/baselines'
import { useGetQRAAssessmentQuery } from 'app/services/api/qra/assessments'
import {
  IQRAGraphCsvExportParams,
  useExportQRARiskForecastGraphMutation
} from 'app/services/api/qra/graphs'
import { useExportJobContext } from 'common/contexts/useExportJobContext'
import {
  formatWithCommasOrExponential,
  getChartLabelBasedOnRiskUnit,
  makeRUoptions
} from 'common/utils/chart/chartUtils'

import GraphContainerCard from 'features/RiskManagement/Components/Graphs/Components/GraphContainerCard'
import GraphTypeToggle from 'features/RiskManagement/Components/Graphs/Components/GraphTypeToggle'
import { selectRiskUnit } from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'

import { getChartCsvExportQueryParams } from './Chart.helpers'
import { RiskForecastGraphs } from './RiskForecastGraphs'

export interface IRiskForecastChartYearSeriesItem {
  riskType: string
  value: number
  subThreats?: Array<{
    key: string
    label: string
    value: number
  }>
}

interface IRiskForecastChart {
  years: number[]
  baseSeriesPerYear: IRiskForecastChartYearSeriesItem[][]
  compareSeriesPerYear?: IRiskForecastChartYearSeriesItem[][]
  showLoading: boolean
  s1Label?: string
  s2Label?: string
  isWideDesktop: boolean
  onGraphTypeChange?: (graphType: RiskForecastGraphs) => void
  csvExportParams?: IQRAGraphCsvExportParams
}

// #region helper method
interface IRiskValuesForTypeOverYears {
  [riskTypes: string]: number[]
}

const getRiskValuesPerTypeOverYears = (
  seriesPerYear: IRiskForecastChartYearSeriesItem[][]
): IRiskValuesForTypeOverYears => {
  const riskValuesForType: IRiskValuesForTypeOverYears = {}

  seriesPerYear.forEach((seriesForYear, yearIdx) => {
    seriesForYear.forEach(({ riskType, value }) => {
      riskValuesForType[riskType] ??= []

      // sparse insertion
      riskValuesForType[riskType][yearIdx] = value
    })
  })

  return riskValuesForType
}
// #endregion helper method

type RiskForecastTooltipValue = {
  label: string
  value: number
}

type RiskForecastTooltipDetail = {
  key: string
  label: string
  values: RiskForecastTooltipValue[]
}

type RiskForecastTooltipDetails = {
  title: string
  items: RiskForecastTooltipDetail[]
  totals: RiskForecastTooltipValue[]
}

type RiskForecastTotalDatum = {
  year: string
  scenario?: string
  [key: string]: string | number | undefined
}

type RiskForecastTooltipRenderer = NonNullable<
  BarChartProps<RiskForecastTotalDatum>['renderTooltip']
>

interface RiskForecastChartContentProps {
  animateGraphTransition: boolean
  byThreatData: RiskForecastTotalDatum[]
  byThreatThreatKeys: string[]
  byThreatLegendState: ReturnType<typeof useBarLegendState>
  chartHeight: number
  formatRiskValue: (value: number) => string
  graphType: RiskForecastGraphs
  hasCompareSeries: boolean
  renderByThreatTooltip: RiskForecastTooltipRenderer
  renderTotalTooltip: RiskForecastTooltipRenderer
  showLoading: boolean
  totalData: RiskForecastTotalDatum[]
  totalDataKeys: string[]
  totalLegendState: ReturnType<typeof useBarLegendState>
  yAxisLabel: string
}

type RiskForecastLegendState = ReturnType<typeof useBarLegendState>

type RiskForecastByThreatChartProps = Pick<
  RiskForecastChartContentProps,
  | 'animateGraphTransition'
  | 'byThreatData'
  | 'byThreatThreatKeys'
  | 'byThreatLegendState'
  | 'chartHeight'
  | 'formatRiskValue'
  | 'hasCompareSeries'
  | 'renderByThreatTooltip'
  | 'yAxisLabel'
>

type RiskForecastTotalChartProps = Pick<
  RiskForecastChartContentProps,
  | 'animateGraphTransition'
  | 'chartHeight'
  | 'formatRiskValue'
  | 'hasCompareSeries'
  | 'renderTotalTooltip'
  | 'totalData'
  | 'totalDataKeys'
  | 'totalLegendState'
  | 'yAxisLabel'
>

const ExportableStackedGroupBarChart = <D extends object>({
  displayComparisonLegendOnExport = false,
  exportColors,
  exportDataKeys,
  ...chartProps
}: StackedGroupBarChartProps<D> & {
  displayComparisonLegendOnExport?: boolean
  exportColors: string[]
  exportDataKeys: string[]
}) => {
  const parentRef = useStackedGroupBarChartPngExport({
    ...chartProps,
    colors: exportColors,
    dataKeys: exportDataKeys,
    displayComparisonLegend: displayComparisonLegendOnExport
  })

  return (
    <div ref={parentRef} className='h-full w-full min-w-0'>
      <StackedGroupBarChart<D> {...chartProps} />
    </div>
  )
}

const ExportableBarChart = <D extends object>({
  exportColors,
  exportDataKeys,
  withLegendOnExport = false,
  ...chartProps
}: BarChartProps<D> & {
  exportColors: string[]
  exportDataKeys: string[]
  withLegendOnExport?: boolean
}) => {
  const parentRef = useBarChartPngExport({
    ...chartProps,
    colors: exportColors,
    dataKeys: exportDataKeys,
    withLegend: withLegendOnExport
  })

  return (
    <div ref={parentRef} className='h-full w-full min-w-0'>
      <BarChart<D> {...chartProps} />
    </div>
  )
}

const EXPORT_UNAVAILABLE_MESSAGE =
  'Chart image export is unavailable right now.'

const CHART_TITLE = 'Risk Forecast'
const RISK_FORECAST_BAR_WIDTH = 48
const RISK_FORECAST_COMPARE_BAR_WIDTH = 32
const RISK_FORECAST_DEFAULT_GROUP_GAP = 5
const RISK_FORECAST_COMPARE_THREAT_GROUP_GAP = 4

const getExportLegendSelection = (
  legendState: RiskForecastLegendState,
  fallbackDataKeys: string[]
) => ({
  colors:
    legendState.activeColors.length > 0
      ? legendState.activeColors
      : [...TIMP_CHART_COLORS],
  dataKeys:
    legendState.activeDataKeys.length > 0
      ? legendState.activeDataKeys
      : fallbackDataKeys
})

const RiskForecastLoading = () => (
  <div className='flex h-full w-full flex-col items-center justify-center gap-2 text-gray-700'>
    <Spinner />
    <div>Loading...</div>
  </div>
)

const RiskForecastTotalLegend = ({ labels }: { labels: string[] }) => (
  <div className='flex items-center gap-4 text-sm'>
    {labels.map((label, index) => (
      <div key={label} className='flex items-center gap-1 leading-none'>
        <span
          aria-label={`${label} color indicator`}
          className='size-3 shrink-0 rounded-sm'
          style={{ backgroundColor: TIMP_CHART_COLORS[index] }}
        />
        <span>{label}</span>
      </div>
    ))}
  </div>
)

const getRiskForecastTooltipDetails = (
  baselineItem: IRiskForecastChartYearSeriesItem | undefined,
  comparisonItem: IRiskForecastChartYearSeriesItem | undefined,
  primarySeriesLabel: string,
  secondarySeriesLabel: string
): RiskForecastTooltipDetails | undefined => {
  const subThreatsByKey = new Map(
    [
      ...(baselineItem?.subThreats ?? []),
      ...(comparisonItem?.subThreats ?? [])
    ].map((subThreat) => [subThreat.key, subThreat])
  )

  const items = [...subThreatsByKey.values()].map((subThreat) => ({
    key: subThreat.key,
    label: subThreat.label,
    values: [
      {
        label: primarySeriesLabel,
        value:
          baselineItem?.subThreats?.find(({ key }) => key === subThreat.key)
            ?.value ?? 0
      },
      ...(comparisonItem == null
        ? []
        : [
            {
              label: secondarySeriesLabel,
              value:
                comparisonItem.subThreats?.find(
                  ({ key }) => key === subThreat.key
                )?.value ?? 0
            }
          ])
    ]
  }))

  if (items.length < 1) {
    return undefined
  }

  return {
    title: baselineItem?.riskType ?? comparisonItem?.riskType ?? '',
    items,
    totals: [
      { label: primarySeriesLabel, value: baselineItem?.value ?? 0 },
      ...(comparisonItem == null
        ? []
        : [{ label: secondarySeriesLabel, value: comparisonItem.value }])
    ]
  }
}

const RiskForecastTooltip = ({
  details,
  formatRiskValue,
  year
}: {
  details: RiskForecastTooltipDetails
  formatRiskValue: (value: number) => string
  year: string
}) => {
  const showScenarioLabels = details.totals.length > 1

  if (showScenarioLabels) {
    return (
      <div className='px-2 py-1 leading-6'>
        <h4 className='font-semibold'>
          {details.title} - {year}
        </h4>
        <table className='border-collapse'>
          <thead>
            <tr>
              <th className='pl-4 pr-4 text-left font-semibold' scope='col'>
                By Subthreat
              </th>
              {details.totals.map((total, index) => (
                <th
                  key={`heading-${total.label ?? index}`}
                  className='pl-4 text-right font-semibold'
                  scope='col'
                >
                  {total.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {details.items.map((item) => (
              <tr key={item.key}>
                <th className='pl-4 pr-4 text-left font-normal' scope='row'>
                  {item.label}
                </th>
                {item.values.map((itemValue, index) => (
                  <td
                    key={`${item.key}-${itemValue.label ?? index}`}
                    className='pl-4 text-right tabular-nums'
                  >
                    {formatRiskValue(itemValue.value)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className='font-semibold'>
              <th className='pr-4 text-left' scope='row'>
                Total Risk
              </th>
              {details.totals.map((total, index) => (
                <td
                  key={`total-${total.label ?? index}`}
                  className='pl-4 text-right tabular-nums'
                >
                  {formatRiskValue(total.value)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    )
  }

  return (
    <div className='px-2 py-1 leading-6'>
      <h4 className='font-semibold'>
        {details.title} - {year}
      </h4>
      <div className='pl-4 font-semibold'>By Subthreat</div>
      {details.items.map((item) => (
        <div key={item.key} className='flex justify-between gap-4 pl-4'>
          <span>{item.label}</span>
          <span className='flex gap-4 tabular-nums'>
            {item.values.map((itemValue, index) => (
              <span key={`${item.key}-${itemValue.label ?? index}`}>
                {formatRiskValue(itemValue.value)}
              </span>
            ))}
          </span>
        </div>
      ))}
      <div className='flex justify-between gap-4 font-semibold'>
        <span>Total Risk</span>
        <span className='flex gap-4 tabular-nums'>
          {details.totals.map((total, index) => (
            <span key={`total-${total.label ?? index}`}>
              {formatRiskValue(total.value)}
            </span>
          ))}
        </span>
      </div>
    </div>
  )
}

const RiskForecastByThreatChart = ({
  animateGraphTransition,
  byThreatData,
  byThreatThreatKeys,
  byThreatLegendState,
  chartHeight,
  formatRiskValue,
  hasCompareSeries,
  renderByThreatTooltip,
  yAxisLabel
}: RiskForecastByThreatChartProps) => {
  const exportSelection = getExportLegendSelection(
    byThreatLegendState,
    byThreatThreatKeys
  )
  const exportSignature = [
    RiskForecastGraphs.ByThreat,
    exportSelection.dataKeys.join('|'),
    exportSelection.colors.join('|')
  ].join('::')

  return (
    <div
      className={
        animateGraphTransition ? 'risk-forecast-chart-enter' : undefined
      }
    >
      <ExportableStackedGroupBarChart<RiskForecastTotalDatum>
        key={exportSignature}
        barWidth={
          hasCompareSeries
            ? RISK_FORECAST_COMPARE_BAR_WIDTH
            : RISK_FORECAST_BAR_WIDTH
        }
        chartName='risk-forecast-by-threat'
        colors={[...TIMP_CHART_COLORS]}
        data={byThreatData}
        dataKeys={byThreatThreatKeys}
        exportColors={exportSelection.colors}
        exportDataKeys={exportSelection.dataKeys}
        groupInnerGap={
          hasCompareSeries
            ? RISK_FORECAST_COMPARE_THREAT_GROUP_GAP
            : RISK_FORECAST_DEFAULT_GROUP_GAP
        }
        x1Padding={hasCompareSeries ? 0 : 0.2}
        height={chartHeight}
        legendAlign='left'
        legendAnchor='container-start'
        legendState={byThreatLegendState}
        scrollbarVisibility='auto'
        withLegend
        noLabelWithComparisonLegend={false}
        displayComparisonLegend={false}
        displayComparisonLegendOnExport={hasCompareSeries}
        renderTooltip={renderByThreatTooltip}
        xAccessor={(datum) => datum.year}
        x1Accessor={
          hasCompareSeries ? (datum) => datum.scenario ?? '' : undefined
        }
        xAxisLabel='Year'
        xAxisLabelAnchor='start'
        yAxisLabel={yAxisLabel}
        yDomainPadding={1}
        yTickFormatter={(value) => formatRiskValue(Number(value))}
        yTicks={4}
      />
    </div>
  )
}

const RiskForecastTotalChart = ({
  animateGraphTransition,
  chartHeight,
  formatRiskValue,
  hasCompareSeries,
  renderTotalTooltip,
  totalData,
  totalDataKeys,
  totalLegendState,
  yAxisLabel
}: RiskForecastTotalChartProps) => {
  const exportSelection = getExportLegendSelection(
    totalLegendState,
    totalDataKeys
  )

  return (
    <div
      className={`risk-forecast-total-chart${
        animateGraphTransition ? ' risk-forecast-chart-enter' : ''
      }`}
    >
      <ExportableBarChart<RiskForecastTotalDatum>
        chartName='risk-forecast-total'
        barWidth={
          hasCompareSeries
            ? RISK_FORECAST_COMPARE_BAR_WIDTH
            : RISK_FORECAST_BAR_WIDTH
        }
        colors={[...TIMP_CHART_COLORS]}
        data={totalData}
        dataKeys={totalDataKeys}
        exportColors={exportSelection.colors}
        exportDataKeys={exportSelection.dataKeys}
        withLegendOnExport={hasCompareSeries}
        groupInnerGap={hasCompareSeries ? RISK_FORECAST_DEFAULT_GROUP_GAP : 0}
        height={chartHeight}
        legendState={totalLegendState}
        renderTooltip={renderTotalTooltip}
        xAccessor={(datum) => datum.year}
        withLegend={false}
        xAxisLabel='Year'
        xAxisLabelAnchor='start'
        yAxisLabel={yAxisLabel}
        yTickFormatter={(value) => formatRiskValue(Number(value))}
      />
    </div>
  )
}

const RiskForecastChartContent = ({
  animateGraphTransition,
  byThreatData,
  byThreatThreatKeys,
  byThreatLegendState,
  chartHeight,
  formatRiskValue,
  graphType,
  hasCompareSeries,
  renderByThreatTooltip,
  renderTotalTooltip,
  showLoading,
  totalData,
  totalDataKeys,
  totalLegendState,
  yAxisLabel
}: RiskForecastChartContentProps) => {
  if (showLoading) {
    return <RiskForecastLoading />
  }

  if (graphType === RiskForecastGraphs.ByThreat) {
    return (
      <RiskForecastByThreatChart
        animateGraphTransition={animateGraphTransition}
        byThreatData={byThreatData}
        byThreatThreatKeys={byThreatThreatKeys}
        byThreatLegendState={byThreatLegendState}
        chartHeight={chartHeight}
        formatRiskValue={formatRiskValue}
        hasCompareSeries={hasCompareSeries}
        renderByThreatTooltip={renderByThreatTooltip}
        yAxisLabel={yAxisLabel}
      />
    )
  }

  return (
    <RiskForecastTotalChart
      animateGraphTransition={animateGraphTransition}
      chartHeight={chartHeight}
      formatRiskValue={formatRiskValue}
      hasCompareSeries={hasCompareSeries}
      renderTotalTooltip={renderTotalTooltip}
      totalData={totalData}
      totalDataKeys={totalDataKeys}
      totalLegendState={totalLegendState}
      yAxisLabel={yAxisLabel}
    />
  )
}

const RiskForecastChart = ({
  years,
  baseSeriesPerYear,
  compareSeriesPerYear,
  showLoading,
  s1Label,
  s2Label,
  isWideDesktop,
  onGraphTypeChange,
  csvExportParams
}: IRiskForecastChart) => {
  const { assessmentId } = useParams()
  const { currentData: currentAssessment } = useGetQRAAssessmentQuery(
    assessmentId ?? skipToken
  )

  const riskUnit = useAppSelector(selectRiskUnit)
  const { downloadImage, isReady: isChartPngExportReady } = usePngExport()

  const [graphType, setGraphType] = useState<RiskForecastGraphs>(
    RiskForecastGraphs.ByThreat
  )

  const [animateGraphTransition, setAnimateGraphTransition] = useState(false)

  const s1Values = useMemo(() => {
    return baseSeriesPerYear.map((seriesForYear) =>
      seriesForYear.reduce((total, { value }) => total + value, 0)
    )
  }, [baseSeriesPerYear])

  const s2Values = useMemo(() => {
    return (compareSeriesPerYear ?? []).map((seriesForYear) =>
      seriesForYear.reduce((total, { value }) => total + value, 0)
    )
  }, [compareSeriesPerYear])

  const formatterOptions = makeRUoptions('ru')
  const formatRiskValue = (value: number) =>
    formatWithCommasOrExponential(value, formatterOptions)

  const yAxisLabel = getChartLabelBasedOnRiskUnit(riskUnit)

  const primarySeriesLabel = s1Label ?? 'Baseline'
  const secondarySeriesLabel = s2Label ?? 'Compare'

  const baseValuesByThreat = useMemo(() => {
    return getRiskValuesPerTypeOverYears(baseSeriesPerYear)
  }, [baseSeriesPerYear])

  const compareValuesByThreat = useMemo(() => {
    return getRiskValuesPerTypeOverYears(compareSeriesPerYear ?? [])
  }, [compareSeriesPerYear])

  const hasCompareSeries = useMemo(() => {
    return (
      compareSeriesPerYear?.some((seriesForYear) => seriesForYear.length > 0) ??
      false
    )
  }, [compareSeriesPerYear])

  const byThreatThreatKeys = useMemo(() => {
    return Array.from(
      new Set([
        ...Object.keys(baseValuesByThreat),
        ...Object.keys(compareValuesByThreat)
      ])
    )
  }, [baseValuesByThreat, compareValuesByThreat])

  const legendColors = useMemo(() => [...TIMP_CHART_COLORS], [])

  const byThreatLegendState = useBarLegendState({
    colors: legendColors,
    dataKeys: byThreatThreatKeys
  })

  const byThreatData = useMemo(() => {
    return years.flatMap((year, yearIdx): RiskForecastTotalDatum[] => {
      const yearLabel = String(year)

      const baselineDatum: RiskForecastTotalDatum = {
        year: yearLabel,
        scenario: primarySeriesLabel
      }

      byThreatThreatKeys.forEach((threatKey) => {
        baselineDatum[threatKey] = baseValuesByThreat[threatKey]?.[yearIdx] ?? 0
      })

      if (!hasCompareSeries) {
        return [baselineDatum]
      }

      baselineDatum.title = `${yearLabel} – ${primarySeriesLabel}`

      const compareDatum: RiskForecastTotalDatum = {
        year: yearLabel,
        title: `${yearLabel} – ${secondarySeriesLabel}`,
        scenario: secondarySeriesLabel
      }

      byThreatThreatKeys.forEach((threatKey) => {
        compareDatum[threatKey] =
          compareValuesByThreat[threatKey]?.[yearIdx] ?? 0
      })

      return [baselineDatum, compareDatum]
    })
  }, [
    baseValuesByThreat,
    byThreatThreatKeys,
    compareValuesByThreat,
    hasCompareSeries,
    primarySeriesLabel,
    secondarySeriesLabel,
    years
  ])

  const renderByThreatTooltip: RiskForecastTooltipRenderer = ({
    datum,
    tooltipData
  }) => {
    const yearIndex = years.findIndex((year) => String(year) === datum.year)
    const threatKey = tooltipData.stackKey ?? tooltipData.key

    if (yearIndex < 0) {
      return null
    }

    const details = getRiskForecastTooltipDetails(
      baseSeriesPerYear[yearIndex]?.find(
        ({ riskType }) => riskType === threatKey
      ),
      hasCompareSeries
        ? compareSeriesPerYear?.[yearIndex]?.find(
            ({ riskType }) => riskType === threatKey
          )
        : undefined,
      primarySeriesLabel,
      secondarySeriesLabel
    )

    return details == null ? null : (
      <RiskForecastTooltip
        details={details}
        formatRiskValue={formatRiskValue}
        year={datum.year}
      />
    )
  }

  const renderTotalTooltip: RiskForecastTooltipRenderer = ({ datum }) => {
    const yearIndex = years.findIndex((year) => String(year) === datum.year)

    if (yearIndex < 0) {
      return null
    }

    const values = [
      {
        color: TIMP_CHART_COLORS[0],
        label: primarySeriesLabel,
        value: s1Values[yearIndex] ?? 0
      },
      ...(hasCompareSeries
        ? [
            {
              color: TIMP_CHART_COLORS[1],
              label: secondarySeriesLabel,
              value: s2Values[yearIndex] ?? 0
            }
          ]
        : [])
    ]

    return (
      <div className='px-2 py-1 leading-6'>
        <h4 className='font-semibold'>{datum.year}</h4>
        {values.map(({ color, label, value }) => (
          <div key={label} className='flex justify-between gap-4'>
            <span className='flex items-center gap-1'>
              <span
                aria-label={`${label} color indicator`}
                className='size-3 shrink-0 rounded-sm'
                style={{ backgroundColor: color }}
              />
              <span>{label}</span>
            </span>
            <span className='tabular-nums'>{formatRiskValue(value)}</span>
          </div>
        ))}
      </div>
    )
  }

  const totalDataKeys = useMemo(() => {
    const keys = [primarySeriesLabel]

    if (hasCompareSeries) {
      keys.push(secondarySeriesLabel)
    }

    return keys
  }, [hasCompareSeries, primarySeriesLabel, secondarySeriesLabel])

  const totalLegendState = useBarLegendState({
    colors: legendColors,
    dataKeys: totalDataKeys
  })

  const activeLegendState =
    graphType === RiskForecastGraphs.ByThreat
      ? byThreatLegendState
      : totalLegendState

  const disableExportMenuItems = activeLegendState.activeDataKeys.length === 0
  const exportPngMenuLabel = 'Export chart as image'
  const exportCsvMenuLabel = 'Export chart as data'

  const totalData = useMemo(() => {
    return years.map((year, index): RiskForecastTotalDatum => {
      const datum: RiskForecastTotalDatum = {
        year: String(year),
        [primarySeriesLabel]: s1Values[index] ?? 0
      }

      if (hasCompareSeries) {
        datum[secondarySeriesLabel] = s2Values[index] ?? 0
      }

      return datum
    })
  }, [
    years,
    hasCompareSeries,
    primarySeriesLabel,
    s1Values,
    s2Values,
    secondarySeriesLabel
  ])

  const chartHeight = isWideDesktop ? 420 : 360

  const handleGraphTypeChange = (nextGraphType: RiskForecastGraphs) => {
    if (nextGraphType !== graphType) {
      setAnimateGraphTransition(true)
    }

    setGraphType(nextGraphType)
    onGraphTypeChange?.(nextGraphType)
  }

  const getExportFileName = () => {
    let fileName =
      graphType === RiskForecastGraphs.ByThreat
        ? 'Risk_Forecast_By_Threat'
        : 'Risk_Forecast_Total'

    if (currentAssessment?.name) {
      fileName += `_${currentAssessment.name}`
    }

    return fileName
  }

  const handleExportChartAsPng = async () => {
    const fileName = getExportFileName()

    if (!isChartPngExportReady) {
      toast.error(EXPORT_UNAVAILABLE_MESSAGE)

      return
    }

    try {
      await downloadImage({ fileName, options: { title: CHART_TITLE } })
    } catch {
      toast.error(EXPORT_UNAVAILABLE_MESSAGE)
    }
  }

  const { currentData: baseline } = useBaselineInfoQuery('current')

  const [triggerExportRiskForecastGraph] =
    useExportQRARiskForecastGraphMutation()

  const { createDataExportJob } = useExportJobContext()

  const handleExportChartAsCsv = async () => {
    const fileName = getExportFileName()
    const baselineId = baseline?.id
    const enabledThreatLabels = activeLegendState.activeDataKeys
    const forecastChartType =
      graphType === RiskForecastGraphs.ByThreat
        ? RiskForecastGraphs.ByThreat
        : RiskForecastGraphs.Total

    if (csvExportParams) {
      createDataExportJob(
        triggerExportRiskForecastGraph,
        getChartCsvExportQueryParams({
          sortBy: 'default',
          csvExportParams,
          assessmentId,
          baselineId,
          forecastChartType,
          enabledThreatLabels
        }),
        fileName,
        '.csv'
      )
    }
  }

  return (
    <GraphContainerCard
      title={CHART_TITLE}
      optionsComponent={
        <div className='flex items-center gap-2'>
          {hasCompareSeries &&
            (graphType === RiskForecastGraphs.Total ? (
              <RiskForecastTotalLegend
                labels={[primarySeriesLabel, secondarySeriesLabel]}
              />
            ) : (
              <ComparisonLegend
                labels={[primarySeriesLabel, secondarySeriesLabel]}
              />
            ))}
          <GraphTypeToggle
            graphType={graphType}
            setGraphType={handleGraphTypeChange}
          />
        </div>
      }
      actionMenuItems={[
        {
          children: exportPngMenuLabel,
          disabled: disableExportMenuItems,
          onSelect: () => {
            void handleExportChartAsPng()
          }
        },
        {
          children: exportCsvMenuLabel,
          disabled: disableExportMenuItems,
          onSelect: () => {
            void handleExportChartAsCsv()
          }
        }
      ]}
    >
      <RiskForecastChartContent
        animateGraphTransition={animateGraphTransition}
        byThreatData={byThreatData}
        byThreatThreatKeys={byThreatThreatKeys}
        byThreatLegendState={byThreatLegendState}
        chartHeight={chartHeight}
        formatRiskValue={formatRiskValue}
        graphType={graphType}
        hasCompareSeries={hasCompareSeries}
        renderByThreatTooltip={renderByThreatTooltip}
        renderTotalTooltip={renderTotalTooltip}
        showLoading={showLoading}
        totalData={totalData}
        totalDataKeys={totalDataKeys}
        totalLegendState={totalLegendState}
        yAxisLabel={yAxisLabel}
      />
    </GraphContainerCard>
  )
}

export default RiskForecastChart
